// Connectivity awareness (desktop offline mode, §15). Tracks whether the backend is reachable so
// the UI degrades gracefully (read-only, work from memory) instead of throwing/redirecting when the
// network drops mid-session — e.g. a VPN that kills internet. Three signals feed the state:
//   1. navigator.onLine + window online/offline events,
//   2. API failures (a fetch that never gets a response) — wired in api/client.ts request(),
//   3. an explicit reachability probe (probeReachable in api/client.ts) used at bootstrap.
import { create } from "zustand";

type NetState = "online" | "offline";

interface ConnectivityState {
  state: NetState;
  lastFailureAt: number | null;
  /** A request reached the server (any HTTP status) → online. */
  markApiSuccess(): void;
  /** A request failed at the network level (no response) → offline. */
  markApiFailure(): void;
  setNavigator(online: boolean): void;
  /** Attach window online/offline listeners; returns an unlisten fn. */
  init(): () => void;
}

export const useConnectivity = create<ConnectivityState>((set, get) => ({
  state: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online",
  lastFailureAt: null,
  markApiSuccess: () => set({ state: "online", lastFailureAt: null }),
  markApiFailure: () => set({ state: "offline", lastFailureAt: Date.now() }),
  setNavigator: (online) => set({ state: online ? "online" : "offline" }),
  init: () => {
    if (typeof window === "undefined") return () => {};
    const on = () => get().setNavigator(true);
    const off = () => get().setNavigator(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  },
}));

/** Reactive selector: true when the backend is currently considered unreachable. */
export function useIsOffline(): boolean {
  return useConnectivity((s) => s.state === "offline");
}
